import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";

const NAV = [
  { to: "/", key: "home" },
  { to: "/restaurant", key: "restaurant" },
  { to: "/menu", key: "menu" },
  { to: "/gallery", key: "gallery" },
  { to: "/reservation", key: "reservation" },
  { to: "/events", key: "events" },
  { to: "/reviews", key: "reviews" },
  { to: "/blog", key: "blog" },
  { to: "/contact", key: "contact" },
];

export default function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-[#050505]/95 backdrop-blur-md border-b border-[#D4AF37]/15 py-3" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 leading-none shrink-0">
          <img src="/logo.png" alt="Restaurant Le Dix Sept — Mulhouse" className="h-12 lg:h-14 w-auto" />
          <span className="hidden sm:flex lg:hidden xl:flex flex-col">
            <span className="font-serif-luxe text-lg xl:text-xl tracking-wide text-[#D4AF37] whitespace-nowrap">Le Dix Sept</span>
            <span className="text-[9px] tracking-[0.32em] uppercase text-[#F8F5EF]/60 mt-1">Mulhouse</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-7" data-testid="nav-desktop">
          {NAV.map(item => (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `nav-link text-[11px] tracking-[0.22em] uppercase font-light text-[#F8F5EF]/80 hover:text-[#D4AF37] transition-colors whitespace-nowrap ${isActive ? "active text-[#D4AF37]" : ""}`
              }
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <span className="hidden lg:block w-px h-5 bg-[#D4AF37]/25" />
          <LanguageSwitcher />
          <Link to="/reservation" data-testid="header-reserve-btn" className="hidden lg:inline-flex btn-gold whitespace-nowrap">
            {t("nav.reserve")}
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#D4AF37] p-1"
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#050505] border-t border-[#D4AF37]/15 mt-3">
          <nav className="flex flex-col py-4">
            {NAV.map(item => (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`mobile-nav-${item.key}`}
                className={({ isActive }) =>
                  `px-6 py-3 text-sm tracking-[0.2em] uppercase border-b border-[#D4AF37]/10 ${isActive ? "text-[#D4AF37]" : "text-[#F8F5EF]/80"}`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <Link to="/reservation" className="mx-6 mt-4 btn-gold justify-center" data-testid="mobile-reserve-btn">
              {t("nav.reserve")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
